import React, { useState, useRef, useEffect, useCallback } from 'react'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import Dropdown from 'react-bootstrap/Dropdown'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagic, faArrowUp, faTimes, faUndo, faCheck, faExclamationTriangle, faRedo, faTasks, faPlus, faHistory, faStop } from '@fortawesome/free-solid-svg-icons'
import { useSendAssistantMessage, useAssistantConversation, useAssistantConversations, useUndoAgentAction, useCancelAssistantTurn } from 'api/hooks/use-report-assistant'
import MarkdownMessage from 'shared/markdown-message'

// The agent's evolving plan, rendered as a checklist. Only the latest plan snapshot is shown.
const PlanChecklist = ({ steps }) => (
  <div className="mb-3 rounded-3 border p-2" style={{ backgroundColor: '#f8f9fa' }}>
    <div className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1E2E48' }}>
      <FontAwesomeIcon icon={faTasks} style={{ fontSize: '0.75rem' }} />
      Plan
    </div>
    {(steps || []).map((step, i) => {
      const done = step.status === 'done'
      const active = step.status === 'in_progress'
      return (
        <div key={i} className="d-flex align-items-start gap-2 mb-1" style={{ fontSize: '0.76rem' }}>
          <span style={{ marginTop: 2, width: 12, flexShrink: 0, textAlign: 'center' }}>
            {done
              ? <FontAwesomeIcon icon={faCheck} className="text-success" style={{ fontSize: '0.68rem' }} />
              : active
                ? <Spinner animation="border" size="sm" style={{ width: 9, height: 9, borderWidth: 1.5, color: '#1E2E48' }} />
                : <span className="d-inline-block rounded-circle border" style={{ width: 8, height: 8 }} />}
          </span>
          <span
            className={done ? 'text-muted text-decoration-line-through' : ''}
            style={{ color: '#1E2E48', fontWeight: active ? 600 : 400 }}
          >
            {step.text}
          </span>
        </div>
      )
    })}
  </div>
)

// A single applied-action line in the activity feed. Undo is offered only on the latest applied
// action (LIFO), matching the server guard.
const ActionEvent = ({ action, isLatest, onUndo, isUndoing, disabled }) => {
  const undone = action?.status === 'undone'
  return (
    <div className="mb-2 d-flex align-items-start gap-2" style={{ fontSize: '0.78rem', color: '#1E2E48' }}>
      <FontAwesomeIcon
        icon={undone ? faUndo : faCheck}
        className={undone ? 'text-muted' : 'text-success'}
        style={{ fontSize: '0.72rem', marginTop: 3 }}
      />
      <span className={undone ? 'text-muted text-decoration-line-through' : ''} style={{ flex: 1 }}>
        {action?.summary}
      </span>
      {action?.reversible && isLatest && (
        <Button
          size="sm"
          variant="light"
          className="border py-0 px-2 flex-shrink-0"
          style={{ fontSize: '0.72rem', fontWeight: 500, color: '#1E2E48' }}
          onClick={() => onUndo(action.id)}
          disabled={disabled}
          title="Undo this action"
        >
          {isUndoing ? <Spinner animation="border" size="sm" /> : 'Undo'}
        </Button>
      )}
    </div>
  )
}

// Icon-only dropdown toggle (no default caret) for the conversation-history menu.
const HistoryToggle = React.forwardRef(({ onClick }, ref) => (
  <span
    ref={ref}
    role="button"
    aria-label="Conversation history"
    title="Conversation history"
    onClick={onClick}
    className="text-muted"
    style={{ cursor: 'pointer' }}
  >
    <FontAwesomeIcon icon={faHistory} style={{ fontSize: '0.95rem' }} />
  </span>
))
HistoryToggle.displayName = 'HistoryToggle'

function AssistantDrawer({ reportId, datasetCount, conversationId, setConversationId, selectedChart = null, selectedEmptySlot = null, onClearFocus, onClose }) {
  const queryClient = useQueryClient()
  const [input, setInput] = useState('')
  const [undoingId, setUndoingId] = useState(null)
  // The just-sent message, shown instantly as a user bubble while the POST is in flight. The server
  // records the turn in the request, so its response carries the real user_text event and this is
  // cleared the moment it lands — the bubble covers the POST only, never the worker's queue wait.
  const [pendingUserText, setPendingUserText] = useState(null)

  // Past conversations for the history dropdown.
  const { data: conversationList } = useAssistantConversations({ reportId })
  const conversations = conversationList?.conversations ?? []
  const messagesContainerRef = useRef(null)
  const lastActionCountRef = useRef(0)

  const conversationKey = (id) => [`reports/${reportId}/assistant/conversations/${id}`]

  const { mutate: send, isLoading: isSending } = useSendAssistantMessage({ reportId })

  const { mutate: cancelTurn, isLoading: isCancelling } = useCancelAssistantTurn({
    reportId,
    options: {
      onSuccess: () => queryClient.invalidateQueries(conversationKey(conversationId)),
      onError: () => toast.error('Could not stop the assistant')
    }
  })

  const { data: conversation } = useAssistantConversation({
    reportId,
    conversationId,
    options: {
      // Poll while the agent is working; stop once it finishes, pauses for input, or errors.
      refetchInterval: (data) => (data?.status === 'running' ? 1200 : false)
    }
  })

  const STALE_MS = 180000 // matches the server's STALE_RUN_AFTER window for reclaiming a dead turn
  const status = conversation?.status
  const isRunningRaw = isSending || status === 'running'
  // A running turn whose server updated_at has gone quiet past the window means the worker likely
  // died. Treat it as not-running so the user can retry (the server reclaims the stale conversation).
  const updatedAtMs = conversation?.updated_at ? new Date(conversation.updated_at).getTime() : null
  const isStale = isRunningRaw && !isSending && updatedAtMs != null && Date.now() - updatedAtMs > STALE_MS
  const isRunning = isRunningRaw && !isStale
  const isStopping = isCancelling || !!conversation?.cancel_requested
  const awaitingInput = status === 'awaiting_input'
  const events = conversation?.events ?? []
  const agentActions = conversation?.agent_actions ?? []
  const actionsById = Object.fromEntries(agentActions.map((a) => [a.id, a]))
  // Only the most recent applied action is undoable (LIFO) — matches the server's undo guard.
  const latestActionId = agentActions.filter((a) => a.status === 'applied').slice(-1)[0]?.id
  // Show only the most recent plan snapshot; earlier ones collapse into this evolving checklist.
  const latestPlanId = [...events].reverse().find((e) => e.type === 'plan')?.id

  const { mutate: undoAction } = useUndoAgentAction({})

  // Refresh the report canvas whenever the agent applies, or the user undoes, an action. Datasets and
  // charts/rows can both change (create_view adds a dataset; create_chart adds rows). Chart data is
  // refetched too: a re-scoped View (update_view) or a chart's own filters (update_chart) change what
  // a chart shows without changing its data query's key, so nothing else would redraw it.
  const refreshCanvas = useCallback(() => {
    queryClient.invalidateQueries([`reports/${reportId}/report_grid_rows`])
    queryClient.invalidateQueries([`reports/${reportId}/report_datasets`])
    queryClient.invalidateQueries(['report-chart-data', reportId])
  }, [queryClient, reportId])

  useEffect(() => {
    const count = conversation?.agent_actions?.length ?? 0
    if (count !== lastActionCountRef.current) {
      lastActionCountRef.current = count
      refreshCanvas()
    }
  }, [conversation?.agent_actions?.length, refreshCanvas])

  // Keep the feed scrolled to the latest event.
  useEffect(() => {
    const el = messagesContainerRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [events.length, isRunning, pendingUserText])

  const buildSelection = () => ({
    selectedChart: selectedChart
      ? {
          id: selectedChart.id,
          chart_type: selectedChart.chart_type,
          title: selectedChart.title,
          report_dataset_id: selectedChart.report_dataset_id,
          params: selectedChart.params
        }
      : undefined,
    selectedEmptySlot: selectedEmptySlot
      ? { row_id: selectedEmptySlot.rowId, position: selectedEmptySlot.position }
      : undefined
  })

  const submit = (text) => {
    const { selectedChart: chart, selectedEmptySlot: slot } = buildSelection()
    // Render the message immediately; don't wait for the POST to come back.
    setPendingUserText(text)
    send(
      { message: text, conversationId, selectedChart: chart, selectedEmptySlot: slot },
      {
        onSuccess: (data) => {
          setConversationId(data.id)
          queryClient.setQueryData(conversationKey(data.id), data)
          // Refresh the history dropdown so a brand-new conversation appears in it.
          queryClient.invalidateQueries([`reports/${reportId}/assistant/conversations`])
        },
        // A rejected send records nothing server-side, so hand the text back to the composer
        // instead of dropping it along with the optimistic bubble.
        onError: () => {
          setInput((current) => current || text)
          toast.error('Could not send that message')
        },
        // The server response carries the real user_text event, so drop the optimistic bubble.
        onSettled: () => setPendingUserText(null)
      }
    )
  }

  // Retrying means re-sending the turn that failed. The user's text is already in the event feed,
  // so the last user_text event is the thing to replay — no separate copy to keep in sync.
  const lastUserText = [...events].reverse().find((e) => e.type === 'user_text')?.payload?.text
  const canRetry = !isRunning && !!lastUserText
  const handleRetry = () => {
    if (!canRetry) return
    submit(lastUserText)
  }

  const handleNewConversation = () => {
    if (isRunning) return
    setConversationId(null)
    setInput('')
  }

  const handleSelectConversation = (id) => {
    if (isRunning || id === conversationId) return
    setConversationId(id)
  }

  // Drafting during a turn is allowed — sending is not. The composer stays editable so a column
  // forgotten in the original prompt can be written down while the agent works, and the send
  // affordance returns on its own when the turn ends.
  const composerPlaceholder = awaitingInput
    ? 'Answer the assistant…'
    : isRunning
    ? 'Draft your next message…'
    : 'Give the assistant a goal…'

  const handleSend = () => {
    const text = input.trim()
    if (!text || isRunning) return
    setInput('')
    submit(text)
  }

  // The worker finishes its current tool-use round before noticing, so the button stays in a
  // "Stopping…" state until the poll reports the turn actually ended.
  const handleStop = () => {
    if (!conversationId || isStopping) return
    cancelTurn(conversationId)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleOptionSelect = (value) => {
    if (isRunning) return
    submit(value)
  }

  const handleUndo = (agentActionId) => {
    setUndoingId(agentActionId)
    undoAction(agentActionId, {
      onSettled: () => {
        setUndoingId(null)
        refreshCanvas()
        if (conversationId) queryClient.invalidateQueries(conversationKey(conversationId))
      }
    })
  }

  const renderEvent = (event) => {
    const { id, type, payload } = event
    switch (type) {
      case 'user_text':
        return (
          <div key={id} className="mb-2 text-end">
            <span
              className="d-inline-block rounded-3 px-3 py-2 small text-start"
              style={{ backgroundColor: '#1E2E48', color: '#fff', maxWidth: '85%' }}
            >
              {payload.text}
            </span>
          </div>
        )
      case 'assistant_text':
        return (
          <div key={id} className="mb-2">
            <div
              className="d-inline-block rounded-3 px-3 py-2 small text-start"
              style={{ backgroundColor: '#f1f3f5', color: '#1E2E48', maxWidth: '90%' }}
            >
              <MarkdownMessage text={payload.text} />
            </div>
          </div>
        )
      case 'action':
        return (
          <ActionEvent
            key={id}
            action={actionsById[payload.agent_action_id]}
            isLatest={payload.agent_action_id === latestActionId}
            onUndo={handleUndo}
            isUndoing={undoingId === payload.agent_action_id}
            disabled={isRunning || !!undoingId}
          />
        )
      case 'question':
        return (
          <div key={id} className="mb-2">
            <div
              className="d-inline-block rounded-3 px-3 py-2 small text-start"
              style={{ backgroundColor: '#f1f3f5', color: '#1E2E48', maxWidth: '90%' }}
            >
              <MarkdownMessage text={payload.text} />
            </div>
            {(payload.options?.length ?? 0) > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {payload.options.map((opt, i) => (
                  <Button
                    key={i}
                    size="sm"
                    variant="light"
                    className="border"
                    style={{ fontSize: '0.78rem', fontWeight: 500, color: '#1E2E48' }}
                    onClick={() => handleOptionSelect(opt.value)}
                    disabled={!awaitingInput || isRunning}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )
      case 'plan':
        // Render only the newest plan snapshot; older ones are superseded.
        return id === latestPlanId ? <PlanChecklist key={id} steps={payload.steps} /> : null
      case 'error':
        return (
          <div key={id} className="mb-2 d-flex align-items-start gap-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger" style={{ fontSize: '0.8rem', marginTop: 4 }} />
            <span
              className="d-inline-block rounded-3 px-3 py-2 small text-start"
              style={{ backgroundColor: '#fde8e4', color: '#c0392b', maxWidth: '90%' }}
            >
              {payload.text}
              {/* Offered only when retrying could actually work. A bad model id or a missing
                  entitlement is flagged non-retryable by the server, and inviting a retry there
                  would loop the user against something that cannot succeed. */}
              {payload.retryable !== false && canRetry && (
                <button
                  type="button"
                  onClick={handleRetry}
                  className="btn btn-link btn-sm d-block p-0 mt-1 text-decoration-underline"
                  style={{ color: '#c0392b', fontSize: 'inherit' }}
                >
                  <FontAwesomeIcon icon={faRedo} className="me-1" style={{ fontSize: '0.75rem' }} />
                  Retry
                </button>
              )}
            </span>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div
      className="d-flex flex-column bg-white assistant-sidebar"
      style={{
        position: 'fixed',
        top: 72,
        right: '1.5rem',
        bottom: '1.5rem',
        width: 400,
        maxWidth: 'calc(100vw - 3rem)',
        borderRadius: '0.75rem',
        border: '1px solid #dee2e6',
        boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
        zIndex: 1035,
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div className="px-3 py-3 border-bottom d-flex align-items-center justify-content-between flex-shrink-0">
        <div className="d-flex align-items-center gap-3">
          <div
            className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
            style={{ width: 44, height: 44, backgroundColor: '#1E2E48' }}
          >
            <FontAwesomeIcon icon={faMagic} className="text-warning" />
          </div>
          <div>
            <div className="fw-bold" style={{ color: '#1E2E48', fontSize: '0.95rem' }}>Assistant</div>
            <div className="text-muted" style={{ fontSize: '0.78rem' }}>
              Grounded in {datasetCount} dataset{datasetCount !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-3">
          <FontAwesomeIcon
            icon={faPlus}
            role="button"
            aria-label="New conversation"
            title="New conversation"
            onClick={handleNewConversation}
            className="text-muted"
            style={{ cursor: isRunning ? 'not-allowed' : 'pointer', fontSize: '0.95rem', opacity: isRunning ? 0.4 : 1 }}
          />
          <Dropdown align="end">
            <Dropdown.Toggle as={HistoryToggle} />
            <Dropdown.Menu style={{ maxHeight: 320, overflowY: 'auto', minWidth: 260 }}>
              {conversations.length === 0 && (
                <Dropdown.ItemText className="small text-muted">No past conversations</Dropdown.ItemText>
              )}
              {conversations.map((c) => (
                <Dropdown.Item
                  key={c.id}
                  active={c.id === conversationId}
                  onClick={() => handleSelectConversation(c.id)}
                  className="small d-flex align-items-center justify-content-between gap-2"
                  style={{ maxWidth: 320 }}
                >
                  <span className="text-truncate">{c.preview || 'New conversation'}</span>
                  {c.status === 'running' && <Spinner animation="border" size="sm" className="flex-shrink-0" />}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <FontAwesomeIcon
            icon={faTimes}
            role="button"
            aria-label="Close assistant"
            onClick={onClose}
            className="text-muted"
            style={{ cursor: 'pointer', fontSize: '1rem' }}
          />
        </div>
      </div>

      {/* Activity feed */}
      <div ref={messagesContainerRef} className="p-3 flex-grow-1 overflow-auto">
        <div className="rounded-3 p-3 mb-3" style={{ backgroundColor: '#f1f3f5', maxWidth: '90%' }}>
          <p className="small mb-0" style={{ color: '#1E2E48', lineHeight: 1.6 }}>
            I'm your report assistant. Tell me a goal — like &ldquo;build a monthly spend overview&rdquo; or
            &ldquo;add a top-5 vendors chart&rdquo; — and I'll work through it on the report. You can undo any step.
          </p>
        </div>

        {events.map(renderEvent)}

        {pendingUserText != null && (
          <div className="mb-2 text-end">
            <span
              className="d-inline-block rounded-3 px-3 py-2 small text-start"
              style={{ backgroundColor: '#1E2E48', color: '#fff', maxWidth: '85%' }}
            >
              {pendingUserText}
            </span>
          </div>
        )}

        {isRunning && (
          <div className="mb-2">
            <span
              className="d-inline-block rounded-3 px-3 py-2"
              style={{ backgroundColor: '#f1f3f5' }}
            >
              <Spinner animation="grow" size="sm" style={{ color: '#1E2E48' }} />
            </span>
          </div>
        )}

        {isStale && (
          <div className="mb-2 d-flex align-items-start gap-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning" style={{ fontSize: '0.8rem', marginTop: 4 }} />
            <span
              className="d-inline-block rounded-3 px-3 py-2 small text-start"
              style={{ backgroundColor: '#fff3cd', color: '#856404', maxWidth: '90%' }}
            >
              This is taking longer than expected and may have stalled. You can send your request again.
            </span>
          </div>
        )}
      </div>

      {/* Focus chip + input */}
      <div className="border-top flex-shrink-0" style={{ backgroundColor: '#f8f9fa' }}>
        {(selectedChart || selectedEmptySlot) && (
          <div
            className="px-3 pt-2 pb-2 d-flex align-items-center gap-1"
            style={{ fontSize: '0.72rem', color: '#6c757d' }}
          >
            <span
              className="rounded-1 ps-2 pe-1 py-1 d-inline-flex align-items-center gap-2"
              style={{ backgroundColor: '#fff3cd', color: '#856404', fontWeight: 500 }}
            >
              {selectedChart
                ? `Focused: ${selectedChart.title || selectedChart.chart_type}`
                : 'Focused: Empty slot'}
              {onClearFocus && (
                <FontAwesomeIcon
                  icon={faTimes}
                  role="button"
                  aria-label="Clear focused element"
                  title="Clear focus"
                  onClick={onClearFocus}
                  style={{ cursor: 'pointer', fontSize: '0.7rem' }}
                />
              )}
            </span>
          </div>
        )}
        <div className="px-3 pb-3 pt-3">
          <div className="d-flex align-items-end gap-2">
            <Form.Control
              as="textarea"
              rows={4}
              placeholder={composerPlaceholder}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="border bg-white"
              // Grows with what's typed but keeps a floor of roughly four lines, since a goal for the
              // assistant is usually a paragraph rather than a one-liner.
              style={{ resize: 'vertical', fontSize: '0.85rem', minHeight: 92 }}
            />
            {/* Distinct keys so React remounts rather than reusing one DOM node across the swap.
                Reused, the node kept its focus: clicking Send left the keyboard on the button that
                became Stop, and the next Enter cancelled the turn it had just started. Stopping
                should take a deliberate click. */}
            {isRunning ? (
              <Button
                key="stop"
                variant="light"
                className="flex-shrink-0 d-flex align-items-center justify-content-center p-0 border"
                style={{ width: 40, height: 40 }}
                onClick={handleStop}
                disabled={isStopping}
                aria-label="Stop the assistant"
                title={isStopping ? 'Stopping…' : 'Stop'}
              >
                {isStopping
                  ? <Spinner animation="border" size="sm" />
                  : <FontAwesomeIcon icon={faStop} className="text-danger" />}
              </Button>
            ) : (
              <Button
                key="send"
                variant="primary"
                className="flex-shrink-0 d-flex align-items-center justify-content-center p-0"
                style={{ width: 40, height: 40 }}
                onClick={handleSend}
                disabled={!input.trim()}
                aria-label="Send"
                title="Send"
              >
                <FontAwesomeIcon icon={faArrowUp} />
              </Button>
            )}
          </div>
          {/* The draft is held, not queued: the turn in flight was built without it, and firing it
              the moment that turn ends would send it into a report the user has not seen yet. */}
          {isRunning && !!input.trim() && (
            <div className="small text-muted mt-2">
              Draft saved — send it when the assistant finishes.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AssistantDrawer
